/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

import {Container} from 'flux/utils';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {ControlLabel} from 'react-bootstrap';
import Select, {components} from 'react-select';

import {ContactSelectActions} from './contact-select-actions';
import ContactSelectStore from './contact-select-store';
import {getHint, includesInputWords, isSearchable} from './helpers';
import {MessagesContext} from '../../messages/messages-context';

const SEARCH_DEBOUNCE_MS = 200;
const NOT_LISTED_ID = '-1';
const WARNING_SIGN = '\u26A0\uFE0E';

const ContactSelectMenu = (props) => {
    const {inputValue, noOptionsMessage, options} = props.selectProps;

    if (!options.length) {
        return (
            <components.Menu {...props}>
                {props.children}
            </components.Menu>
        );
    }

    const hint = noOptionsMessage({inputValue});

    return (
        <components.Menu {...props}>
            {hint && (
                <components.NoOptionsMessage
                    {...props}
                    innerProps={{}}
                >
                    {hint}
                </components.NoOptionsMessage>
            )}
            {props.children}
        </components.Menu>
    );
};

const {DropdownIndicator} = components;

ContactSelectMenu.defaultProps = {
    children: null,
    innerProps: {}
};

ContactSelectMenu.propTypes = {
    children: PropTypes.node,
    selectProps: PropTypes.object.isRequired
};


class ContactSelect extends Component {
    static get propTypes() {
        return {
            attr: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            label: PropTypes.oneOfType([PropTypes.object.isRequired, PropTypes.string.isRequired]),
            model: PropTypes.object.isRequired,
            placeholder: PropTypes.string,
            required: PropTypes.bool,
            setter: PropTypes.func.isRequired,
            showNotListed: PropTypes.bool,
            valueKey: PropTypes.string
        };
    }

    static calculateState() {
        return {
            hasError: ContactSelectStore.getState().get('hasError'),
            isLoading: ContactSelectStore.getState().get('isLoading'),
            options: ContactSelectStore.getState().get('options'),
            searchTerm: ContactSelectStore.getState().get('searchTerm')
        };
    }

    static getStores() {
        return [ContactSelectStore];
    }

    static get defaultProps() {
        return {
            disabled: false,
            label: '',
            placeholder: undefined,
            required: false,
            showNotListed: false,
            valueKey: 'id'
        };
    }

    constructor(props) {
        super(props);
        this.state = Object.assign({
            value: this.props.model.get(this.props.attr)
        }, this.constructor.calculateState());

        this.handleInputChange = this.handleInputChange.bind(this);
        this.onChange = this.onChange.bind(this);
        this.optionRenderer = this.optionRenderer.bind(this);
        this.searchTimeout = undefined;
    }

    componentDidMount() {
        ContactSelectActions.clear();
    }

    componentWillReceiveProps(nextProps) {
        this.setState({value: nextProps.model.get(nextProps.attr)});
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.state.hasError !== nextState.hasError ||
            this.state.isLoading !== nextState.isLoading ||
            this.state.options !== nextState.options ||
            this.state.value !== nextState.value ||
            this.state.searchTerm !== nextState.searchTerm) {
            return true;
        }
        return false;
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimeout);
    }


    static contextType = MessagesContext;

    handleInputChange(inputValue) {
        clearTimeout(this.searchTimeout);
        if (!isSearchable(inputValue)) {
            ContactSelectActions.clear();
            return;
        }
        this.searchTimeout = setTimeout(() => ContactSelectActions.get(inputValue), SEARCH_DEBOUNCE_MS);
    }

    onChange(newValue) {
        if (Array.isArray(newValue) && !newValue.length) {
            newValue = undefined;
        }
        this.props.setter(this.props.attr, Immutable.fromJS(newValue));
    }

    optionRenderer(option) {
        let middleName = '';
        if (option.middleName) {
            middleName = `${option.middleName} `;
        }
        return `${option.firstName} ${middleName}${option.lastName}`;
    }

    render() {

        let label = this.props.label;
        if (this.props.required) {
            label = (
                <span>
                    {this.props.label}&nbsp;<span className="text-red">*</span>
                </span>
            );
        }
        label = <ControlLabel>{label}</ControlLabel>;

        // This is done here because this.context isn't available during
        // getDefaultProps().
        let placeholder = this.context.intl.messages['common.form.select.placeholder'];
        if (this.props.placeholder) {
            placeholder = this.props.placeholder;
        }

        let value = '';
        let modelValue = this.state.value;
        if (modelValue !== null && modelValue !== undefined) {
            value = modelValue.toJS();
        }

        let options = this.state.options.toJS();
        if (this.props.showNotListed) {
            const notListed = {
                company: '',
                firstName: 'Not',
                id: NOT_LISTED_ID,
                lastName: 'Listed',
                middleName: ''
            };
            options.push(notListed);
        }

        const errorHint = `${WARNING_SIGN} ${this.context.intl.messages['signup.form.contact.search.error']}`;
        const noMatchesHint = this.context.intl.messages['signup.form.contact.search.no-matches'];
        const tooShortHint = this.context.intl.messages['signup.form.contact.search.too-short'];
        const hasMatches = this.state.options.size > 0;

        const noOptionsMessage = ({inputValue}) => getHint({
            errorHint,
            hasError: this.state.hasError,
            hasMatches,
            inputValue,
            isLoading: this.state.isLoading,
            noMatchesHint,
            searchTerm: this.state.searchTerm,
            tooShortHint
        });

        const filterOption = ({data}, inputValue) => {
            const input = inputValue.trim();
            return data.id === NOT_LISTED_ID || includesInputWords(data, input);
        };

        const styles = {
            option: (base, {data, theme}) => ({
                ...base,
                ...(data.id === NOT_LISTED_ID && {
                    borderTop: `1px solid ${theme.colors.neutral20}`
                })
            }),
            noOptionsMessage: (base) => ({
                ...base,
                textAlign: 'left'
            })
        };

        return (
            <div style={{marginBottom: '20px'}}>
                {label}
                <Select
                    components={{DropdownIndicator, Menu: ContactSelectMenu}}
                    disabled={this.props.disabled}
                    filterOption={filterOption}
                    getOptionLabel={/*istanbul ignore next */data => this.optionRenderer(data)}
                    getOptionValue={/*istanbul ignore next */data => data.id}
                    isLoading={this.state.isLoading}
                    noOptionsMessage={noOptionsMessage}
                    onChange={this.onChange}
                    onInputChange={this.handleInputChange}
                    options={options}
                    placeholder={placeholder}
                    styles={styles}
                    value={value}
                    valueKey={this.props.valueKey}
                    valueRenderer={this.optionRenderer}
                />
            </div>
        );
    }
}

export default Container.create(ContactSelect);
export {ContactSelect, ContactSelectMenu};
